import React, { useEffect, useState } from "react";
import { Product, SerialNumberItem } from "../types/product";

interface SerialNumberModalProps {
  item: Product;
  serialNumbers: SerialNumberItem[];
  selectedSerialNumbers: string[];
  keyword: string;
  isLoading: boolean;
  error: string | null;
  onKeywordChange: (value: string) => void;
  onClose: () => void;
  onConfirm: (serialNumbers: SerialNumberItem[]) => void;
}

const SerialNumberModal: React.FC<SerialNumberModalProps> = ({
  item,
  serialNumbers,
  selectedSerialNumbers,
  keyword,
  isLoading,
  error,
  onKeywordChange,
  onClose,
  onConfirm,
}) => {
  const [selected, setSelected] = useState<string[]>(selectedSerialNumbers);
  const [selectedItems, setSelectedItems] = useState<SerialNumberItem[]>([]);

  useEffect(() => {
    setSelected(selectedSerialNumbers);
  }, [selectedSerialNumbers, item.itemNumber]);

  useEffect(() => {
    setSelectedItems((current) => {
      const itemsBySerial = new Map(
        current.map((serial) => [serial.serialNumber, serial]),
      );
      serialNumbers.forEach((serial) => {
        if (selected.includes(serial.serialNumber)) {
          itemsBySerial.set(serial.serialNumber, serial);
        }
      });
      return Array.from(itemsBySerial.values()).filter((serial) =>
        selected.includes(serial.serialNumber),
      );
    });
  }, [serialNumbers, selected]);

  const toggleSerial = (serialNumber: string) => {
    const serial = serialNumbers.find((value) => value.serialNumber === serialNumber);
    setSelected((current) =>
      current.includes(serialNumber)
        ? current.filter((value) => value !== serialNumber)
        : [...current, serialNumber],
    );
    if (serial) {
      setSelectedItems((current) =>
        current.some((value) => value.serialNumber === serialNumber)
          ? current.filter((value) => value.serialNumber !== serialNumber)
          : [...current, serial],
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold">Pilih Serial Number</h2>
            <p className="text-sm text-gray-600">
              {item.itemName} ({item.itemNumber})
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-2xl leading-none text-gray-500 hover:text-gray-800"
            aria-label="Close serial number modal"
          >
            ×
          </button>
        </div>

        <input
          type="search"
          value={keyword}
          onChange={(event) => onKeywordChange(event.target.value)}
          placeholder="Search serial number"
          className="mb-4 w-full rounded border border-gray-300 p-2"
          aria-label="Search serial number"
        />

        <div className="max-h-80 overflow-y-auto rounded border">
          {isLoading && (
            <p className="p-4 text-center text-gray-600">
              Loading serial numbers...
            </p>
          )}
          {!isLoading && error && (
            <p className="p-4 text-center text-red-600">{error}</p>
          )}
          {!isLoading && !error && serialNumbers.length === 0 && (
            <p className="p-4 text-center text-gray-600">
              No serial numbers found.
            </p>
          )}
          {!isLoading && !error && serialNumbers.length > 0 && (
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                <tr>
                  <th className="p-3">Select</th>
                  <th className="p-3">Serial Number</th>
                  <th className="p-3">Warehouse</th>
                  <th className="p-3">Quantity</th>
                  <th className="p-3">Expired Date</th>
                </tr>
              </thead>
              <tbody>
                {serialNumbers.map((serial) => (
                  <tr key={serial.serialNumber} className="border-t">
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selected.includes(serial.serialNumber)}
                        onChange={() => toggleSerial(serial.serialNumber)}
                        aria-label={`Select ${serial.serialNumber}`}
                      />
                    </td>
                    <td className="p-3 font-medium">{serial.serialNumber}</td>
                    <td className="p-3">{serial.warehouseName || "-"}</td>
                    <td className="p-3">{serial.quantity ?? "-"}</td>
                    <td className="p-3">{serial.expiredDate || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 px-4 py-2 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
            onConfirm(
                selectedItems,
              )
            }
            disabled={selected.length === 0}
            className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            Tambah Pilihan
          </button>
        </div>
      </div>
    </div>
  );
};

export default SerialNumberModal;
